'use client'

// React Imports
import { useMemo } from 'react'

// MUI Imports
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'

// Third-party Imports
import { createColumnHelper } from '@tanstack/react-table'
import { User } from 'lucide-react'

// Component Imports
import PremiumModuleTable from '../common/PremiumModuleTable'

const columnHelper = createColumnHelper()

const CustomerLedgerTable = ({
    data,
    totalRecords,
    currentPage,
    itemsPerPage,
    onPageChange,
    loading
}) => {
    const columns = useMemo(() => [
        columnHelper.accessor('SNo', {
            header: 'S. No.',
            cell: ({ row }) => (
                <Typography sx={{ fontSize: '14px', color: '#334155' }}>
                    {row.original.SNo}
                </Typography>
            )
        }),
        columnHelper.accessor('customer', {
            header: 'Customer',
            cell: ({ row }) => {
                const profileImage = row.original.ProfileImage || '';
                const hasImage = profileImage && !profileImage.includes('img_avatar1.png');
                const avatarUrl = hasImage ? (profileImage.startsWith('http') ? profileImage : `/assets/custom/img/${profileImage}`) : '';

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Avatar
                            src={hasImage ? avatarUrl : undefined}
                            sx={{ 
                                width: 40, 
                                height: 40,
                                ...(!hasImage && {
                                    bgcolor: '#f8fafc',
                                    border: '2px solid #e2e8f0',
                                    color: '#64748b'
                                })
                            }}
                        >
                            {!hasImage && <User size={24} strokeWidth={2} />}
                        </Avatar>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography
                                sx={{
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: '#334155'
                                }}
                            >
                                {row.original.Customer}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                {row.original.MobNo && (
                                    <Typography sx={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <i className="fa fa-phone" style={{ fontSize: '10px' }}></i> {row.original.MobNo}
                                    </Typography>
                                )}
                            </Box>
                            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                                {row.original.Email}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569', mt: 0.5 }}>
                                LeadNo: {row.original.LeadNo}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        }),
        columnHelper.accessor('credit', {
            header: 'Total Credit Amount',
            cell: ({ row }) => (
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#334155'
                    }}
                >
                    {row.original.CurrencySymbol} {parseFloat(row.original.Credit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
            )
        }),
        columnHelper.accessor('totalInvoice', {
            header: 'Total Invoice Amount',
            cell: ({ row }) => (
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#334155'
                    }}
                >
                    {row.original.CurrencySymbol} {parseFloat(row.original.TotalInvoice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
            )
        }),
        columnHelper.accessor('revenue', {
            header: 'Total Paid Amount',
            cell: ({ row }) => (
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#334155'
                    }}
                >
                    {row.original.CurrencySymbol} {parseFloat(row.original.Revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
            )
        }),
        columnHelper.accessor('balance', {
            header: 'Total Balance',
            cell: ({ row }) => {
                return (
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: row.original.Balance < 0 ? '#ef4444' : '#334155'
                        }}
                    >
                        {row.original.CurrencySymbol} {parseFloat(row.original.Balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                )
            }
        })
    ], [])

    return (
        <PremiumModuleTable
            data={data}
            columns={columns}
            totalRecords={totalRecords}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            loading={loading}
        />
    )
}

export default CustomerLedgerTable
